import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useLocation } from "react-router-dom";
import { DashboardWrapper } from "src/components/common/DashboardWrapper";
// `Button` is only used by the commented-out Bank Details edit controls below.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Button, Input } from "src/components/common/FormComponents";
import Snackbar from "src/components/common/Snackbar";
import ViewImages from "src/components/common/ViewImage";
import { MyProfileType, myProfileValue } from "src/constants/sampleData";
import { getStyles } from "src/styles/theme";
import {
  getMyProfileData,
  updateNodalOfficerBankDetail,
} from "src/utils/api/dashboard";
import { dateAndTimeStamp } from "src/utils/helper";
import defaultStyles, { StylesClasses } from "./styles";

type BankDetailForm = {
  accountName: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
};

const emptyBankDetail: BankDetailForm = {
  accountName: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
};

const UserDetails = () => {
  const height = "52px";
  const location = useLocation();
  const userId = location?.state?.userId || "";
  const [isError, setIsError] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isEditingBank, setIsEditingBank] = useState(false);
  const queryClient = useQueryClient();

  const { data: myProfileData } = useQuery(
    ["myProfile", userId],
    () => getMyProfileData(userId),
    {
      onSuccess: (res: AxiosResponse) => {},
      onError: (err: AxiosError) => {
        setIsError(true);
      },
      enabled: !!userId,
    }
  );

  const userData = myProfileData?.data;

  const currAddress = userData?.currentAddress;

  const userDocs = _.groupBy(userData?.userDocuments, "type");
  console.log(userDocs);
  const { control } = useForm<MyProfileType>({
    mode: "onTouched",
    defaultValues: { ...myProfileValue },
  });

  const bankDetail = userData?.forestDetails?.bankDetail;

  // Separate form from the read-only profile form above: this block is the only
  // editable part of the page, and it posts to its own endpoint.
  const {
    control: bankControl,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleSubmit: handleBankSubmit,
    reset: resetBankForm,
    formState: { errors: bankErrors },
  } = useForm<BankDetailForm>({
    mode: "onTouched",
    defaultValues: { ...emptyBankDetail },
  });

  const bankFormValues: BankDetailForm = useMemo(
    () => ({
      accountName: bankDetail?.accountName || "",
      bankName: bankDetail?.bankName || "",
      accountNumber: bankDetail?.accountNumber || "",
      ifscCode: bankDetail?.ifscCode || "",
    }),
    [
      bankDetail?.accountName,
      bankDetail?.bankName,
      bankDetail?.accountNumber,
      bankDetail?.ifscCode,
    ]
  );

  // The profile query resolves after first render, so seed the form once it lands
  // and again after every successful save (invalidateQueries refetches it).
  useEffect(() => {
    resetBankForm(bankFormValues);
  }, [bankFormValues, resetBankForm]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: saveBankDetail, isLoading: isSavingBank } = useMutation(
    (values: BankDetailForm) => updateNodalOfficerBankDetail(userId, values),
    {
      onSuccess: () => {
        setIsEditingBank(false);
        setIsSaved(true);
        queryClient.invalidateQueries(["myProfile", userId]);
      },
      onError: () => setIsError(true),
    }
  );

  const styles = getStyles<StylesClasses>(defaultStyles);

  return (
    <DashboardWrapper
      title="User Profile"
      showBackBtn={true}
      backPath={
        userData?.userType === "ORGANIZATION"
          ? "/users"
          : "/implementing-agency"
      }
    >
      <Box {...styles("wrapper")}>
        <Box {...styles("childWrapper")}>
          <Box {...styles("heading")}>personal Details</Box>
          <Box {...styles("container")}>
            {userData?.userType === "ORGANIZATION" && (
              <Box {...styles("input")}>
                <Input
                  name="authName"
                  label="Entity Type"
                  placeholder="Entity Type"
                  control={control}
                  value={userData?.entityTypeName}
                  customStyles={{ wrapper: defaultStyles.selectWrapper }}
                  height={height}
                  readOnly
                />
              </Box>
            )}

            <Box {...styles("input")}>
              <Input
                name="firstName"
                label={
                  userData?.userType === "ORGANIZATION"
                    ? "Entity Name"
                    : "Agency Name"
                }
                placeholder="Enter full name"
                control={control}
                value={
                  userData?.userType === "ORGANIZATION"
                    ? userData?.firstName
                    : userData?.forestDetails?.name
                }
                height={height}
                customStyles={{ wrapper: defaultStyles.selectWrapper }}
                readOnly
              />
            </Box>

            {userData?.userType === "ORGANIZATION" ? (
              <Box {...styles("input")}>
                <Input
                  name="authName"
                  label="Authorised Representative Name"
                  placeholder="Authorised Representative Name"
                  control={control}
                  value={userData?.nameOfAuthorizedPerson}
                  customStyles={{ wrapper: defaultStyles.selectWrapper }}
                  height={height}
                  readOnly
                />
              </Box>
            ) : (
              <Box {...styles("input")}>
                <Input
                  name="authName"
                  label={
                    userData?.userType === "INDIVIDUAL"
                      ? "Nodal Officer Name"
                      : "Implementing Officer Name"
                  }
                  placeholder="Authorised Representative Name"
                  control={control}
                  value={userData?.firstName}
                  customStyles={{ wrapper: defaultStyles.selectWrapper }}
                  height={height}
                  readOnly
                />
              </Box>
            )}
            <Box {...styles("input")}>
              <Input
                name="authDesig"
                label="Designation"
                placeholder="Designation"
                control={control}
                value={userData?.designation}
                customStyles={{ wrapper: defaultStyles.selectWrapper }}
                height={height}
                readOnly
              />
            </Box>
            <Box {...styles(["input", "withHelperText"])}>
              <Input
                name="email"
                label="Email"
                placeholder="Enter your email"
                value={userData?.email}
                control={control}
                customStyles={{ wrapper: defaultStyles.selectWrapper }}
                readOnly
                height={height}
              />
            </Box>
            <Box {...styles(["input", "withHelperText"])}>
              <Input
                name="phoneNumber"
                label="Phone Number"
                placeholder="Enter your phoneNumber"
                value={userData?.phoneNumber}
                control={control}
                customStyles={{ wrapper: defaultStyles.selectWrapper }}
                readOnly
                height={height}
              />
            </Box>
            {userData?.userType === null && (
              <>
                <Box {...styles("input")}>
                  <Input
                    name="authDesig"
                    label="Division"
                    placeholder="Enter Division"
                    control={control}
                    value={userData?.division}
                    customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    height={height}
                    readOnly
                  />
                </Box>
                <Box {...styles("input")}>
                  <Input
                    name="authDesig"
                    label="District"
                    placeholder="Enter District"
                    control={control}
                    value={userData?.userDistrict}
                    customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    height={height}
                    readOnly
                  />
                </Box>
              </>
            )}
          </Box>
          {userData?.userType === "ORGANIZATION" && (
            <Box {...styles("childWrapper")}>
              <Box {...styles("heading")}>ID Proof Number and Documents</Box>
              <Box {...styles("container")}>
                <Box {...styles("input")}>
                  <Input
                    name="currentAddress.addressLine1"
                    label="ID Proof Number"
                    placeholder="ID Proof Number"
                    control={control}
                    value={userData?.identityProofNumber}
                    height={height}
                    readOnly
                    customStyles={{ wrapper: defaultStyles.selectWrapper }}
                  />
                </Box>
              </Box>
              <ViewImages
                fileUrl={userData?.identityProofImageUrl?.fileUrl}
                label={"Identity Proof Image"}
              />
              {userData?.organizationCertificateImageUrl?.fileUrl && (
                <ViewImages
                  fileUrl={userData?.organizationCertificateImageUrl?.fileUrl}
                  label={"organization Certificate Image"}
                />
              )}
            </Box>
          )}
        </Box>
        <Box {...styles("childWrapper")}>
          <Box {...styles("heading")}>Registered address</Box>
          <Box {...styles("container")}>
            {(userData?.userType === "INDIVIDUAL" ||
              userData?.userType === null) && (
              <>
                <Box {...styles("input")}>
                  <Input
                    name="currentAddress.district"
                    label="state"
                    placeholder="Enter district"
                    control={control}
                    value={currAddress?.state}
                    height={height}
                    readOnly
                    customStyles={{ wrapper: defaultStyles.selectWrapper }}
                  />
                </Box>
                <Box {...styles("input")}>
                  <Input
                    name="currentAddress.district"
                    label="District"
                    placeholder="Enter district"
                    control={control}
                    value={currAddress?.district}
                    height={height}
                    readOnly
                    customStyles={{ wrapper: defaultStyles.selectWrapper }}
                  />
                </Box>
                <Box {...styles("input")}>
                  <Input
                    name="currentAddress.district"
                    label="City"
                    placeholder="Enter district"
                    control={control}
                    value={currAddress?.city}
                    height={height}
                    readOnly
                    customStyles={{ wrapper: defaultStyles.selectWrapper }}
                  />
                </Box>
              </>
            )}
            <Box {...styles("input")}>
              <Input
                name="currentAddress.addressLine1"
                label={
                  userData?.userType === "INDIVIDUAL"
                    ? "Street Address"
                    : "Address"
                }
                placeholder="Enter street address"
                control={control}
                value={currAddress?.addressLine1}
                height={height}
                readOnly
                customStyles={{ wrapper: defaultStyles.selectWrapper }}
              />
            </Box>
            {userData?.userType === "INDIVIDUAL" && (
              <Box {...styles("childWrapper")}>
                <Box {...styles("bankHeadingRow")}>
                  <Box {...styles("bankHeading")}>Bank Details</Box>
                  {/*
                    Editing was a one-off tool for the SNO bank-account
                    correction drive and is switched off now that the accounts
                    are updated. Uncomment to re-enable; everything it needs is
                    still wired (PATCH /v1/user/:id/bank-detail, the form, the
                    mutation), so this block is the only switch.

                  {isEditingBank ? (
                    <Box {...styles("bankActions")}>
                      <Button
                        text="Cancel"
                        variant="outlined"
                        disabled={isSavingBank}
                        onClick={() => {
                          resetBankForm(bankFormValues);
                          setIsEditingBank(false);
                        }}
                      />
                      <Button
                        text="Save"
                        loading={isSavingBank}
                        onClick={handleBankSubmit((values) =>
                          saveBankDetail(values)
                        )}
                      />
                    </Box>
                  ) : (
                    <Button
                      text={bankDetail ? "Edit" : "Add Bank Details"}
                      variant="outlined"
                      onClick={() => setIsEditingBank(true)}
                    />
                  )}
                  */}
                </Box>
                <Box {...styles("container")}>
                  <Box {...styles("input")}>
                    <Input
                      name="accountName"
                      label="Name of the Account"
                      placeholder="e.g. State Nodal Officer GCP Assam"
                      control={bankControl}
                      errors={bankErrors}
                      height={height}
                      readOnly={!isEditingBank}
                      customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="bankName"
                      label="Bank Name"
                      placeholder="Enter bank name"
                      control={bankControl}
                      errors={bankErrors}
                      height={height}
                      readOnly={!isEditingBank}
                      customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="accountNumber"
                      label="Account Number"
                      placeholder="Enter account number"
                      control={bankControl}
                      errors={bankErrors}
                      height={height}
                      readOnly={!isEditingBank}
                      customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="ifscCode"
                      label="IFSC Code"
                      placeholder="Enter IFSC code"
                      control={bankControl}
                      errors={bankErrors}
                      height={height}
                      readOnly={!isEditingBank}
                      customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    />
                  </Box>
                  {userData?.cancelChequeImageUrl?.fileUrl && (
                    <ViewImages
                      fileUrl={userData?.cancelChequeImageUrl?.fileUrl}
                      label={"Cancelled cheque Image"}
                    />
                  )}
                </Box>
              </Box>
            )}
            {userData?.userType === null && (
              <Box {...styles("childWrapper")}>
                <Box {...styles("heading")}>Nodal Officer Details</Box>
                <Box {...styles("container")}>
                  <Box {...styles("input")}>
                    <Input
                      name="currentAddress.district"
                      label="Nodal Officer Name"
                      placeholder="Enter Nodal Officer Name"
                      control={control}
                      value={userData?.nodalOfficerDetail?.firstName}
                      height={height}
                      readOnly
                      customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="currentAddress.district"
                      label="Designation"
                      placeholder="Enter Designation"
                      control={control}
                      value={userData?.nodalOfficerDetail?.designation}
                      height={height}
                      readOnly
                      customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="currentAddress.district"
                      label="Email "
                      placeholder="Enter Email"
                      control={control}
                      value={userData?.nodalOfficerDetail?.email}
                      height={height}
                      readOnly
                      customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="currentAddress.district"
                      label="Phone Number "
                      placeholder="Enter Phone Number"
                      control={control}
                      value={userData?.nodalOfficerDetail?.phoneNumber}
                      height={height}
                      readOnly
                      customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="currentAddress.district"
                      label="State"
                      placeholder="Enter State"
                      control={control}
                      value={userData?.nodalOfficerDetail?.state}
                      height={height}
                      readOnly
                      customStyles={{ wrapper: defaultStyles.selectWrapper }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {userData?.previousUsers && userData.previousUsers.length > 0 && (
          <Box {...styles("childWrapper")}>
            <Box {...styles("heading")}>User History</Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>S. No.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Mobile Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Designation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Joining Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userData.previousUsers.map((item: any, index: number) => (
                    <TableRow
                      key={item.id || index}
                      sx={{ "&:hover": { backgroundColor: "#fafafa" } }}
                    >
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell>{item.name || "—"}</TableCell>
                      <TableCell>{item.email || "—"}</TableCell>
                      <TableCell>{item.phoneNumber || "—"}</TableCell>
                      <TableCell>{item.designation || "—"}</TableCell>
                      <TableCell>
                        {item.joiningDate
                          ? dateAndTimeStamp(item.joiningDate, "IST")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {item.endDate
                          ? dateAndTimeStamp(item.endDate, "IST")
                          : "Active"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
      <Snackbar
        severity="error"
        message="Something Went Wrong"
        open={isError}
        duration={2000}
        resetSnackbar={() => setIsError(false)}
      />
      <Snackbar
        severity="success"
        message="Bank details updated"
        open={isSaved}
        duration={2000}
        resetSnackbar={() => setIsSaved(false)}
      />
    </DashboardWrapper>
  );
};

export default UserDetails;
